import React from "react";
import format from "date-fns/format";
import parseISO from "date-fns/parseISO";

import Divider from "@material-ui/core/Divider";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";

import { EMPTY_STRING } from "common/constants";
import {
  getHorseAgeSexColorValue,
  getHorseOwnerOrBreederName
} from "utils/horse";
import HealthRating from "./HealthRating";
import FlagOnTrack from "./FlagOnTrack";
import { useLoggedInUser } from "components/LoggedInUserProvider";
import BarnNumberPicker from "components/BarnNumberPicker";
import StallNumberPicker from "components/StallNumberPicker";
import { useFacilityBarns } from "components/BarnNumberPicker/useFacilityBarns";
import TrainerListItem from "../../TrainerListItem";
import { VET_HORSE_GET_OPTIONS } from "../options";
import { useCurrentHorseDetails } from "../../useCurrentHorseDetails";

import useStyles from "./styles";

interface Props {
  refetchComments: () => void;
}

const Information = (props: Props) => {
  const { refetchComments } = props;

  const classes = useStyles();
  const { currentUser } = useLoggedInUser();
  const { currentHorse, updateCurrentHorse, isHorseFetching, isHorseLoading } =
    useCurrentHorseDetails({ selectedFields: VET_HORSE_GET_OPTIONS });
  const { facilityBarns, isLoading: isListBarnsLoading } = useFacilityBarns(
    currentHorse?.currentFacility?.id
  );

  const { isRegulatoryVet } = currentUser;
  const isLoading = isHorseFetching || isHorseLoading;

  if (!currentHorse) return null;

  return (
    <>
      <Paper component={List} className={classes.list}>
        <ListItem className={classes.listItem}>
          <Typography>Horse Information</Typography>
        </ListItem>
        <Divider />
        <ListItem className={classes.listItem}>
          <Typography className={classes.listItemLabel}>
            Registration Number
          </Typography>
          <Typography className={classes.listItemValue}>
            {currentHorse.registrationNumber}
          </Typography>
        </ListItem>
        <Divider light />
        <ListItem className={classes.listItem}>
          <Typography className={classes.listItemLabel}>
            Chip ID Number
          </Typography>
          <Typography className={classes.listItemValue}>
            {`${currentHorse.chipNumber || "-"}`}
          </Typography>
        </ListItem>
        <Divider light />
        <ListItem className={classes.listItem}>
          <Typography className={classes.listItemLabel}>
            Date of Birth
          </Typography>
          <Typography className={classes.listItemValue}>
            {currentHorse.birthday &&
              format(parseISO(currentHorse.birthday), "MMMM d, yyyy")}
          </Typography>
        </ListItem>
        <Divider light />
        <ListItem className={classes.listItem}>
          <Typography className={classes.listItemLabel}>
            Color - Sex - Age
          </Typography>
          <Typography className={classes.listItemValue}>
            {getHorseAgeSexColorValue(currentHorse)}
          </Typography>
        </ListItem>
        <Divider light />
        <ListItem className={classes.listItem}>
          <Typography className={classes.listItemLabel}>Dam</Typography>
          <Typography className={classes.listItemValue}>
            {currentHorse.damName}
          </Typography>
        </ListItem>
        <Divider light />
        <ListItem className={classes.listItem}>
          <Typography className={classes.listItemLabel}>Sire</Typography>
          <Typography className={classes.listItemValue}>
            {currentHorse.sireName}
          </Typography>
        </ListItem>
        <Divider light />
        <ListItem className={classes.listItem}>
          <Typography className={classes.listItemLabel}>
            Risk Level/ABC
          </Typography>
          <HealthRating horse={currentHorse} isLoading={isLoading} />
        </ListItem>
        <Divider light />
        {!isRegulatoryVet && (
          <>
            <ListItem className={classes.listItem}>
              <Typography className={classes.listItemLabel}>
                Flag on track
              </Typography>
              <FlagOnTrack
                horse={currentHorse}
                refetchComments={refetchComments}
              />
            </ListItem>
            <Divider light />
          </>
        )}
        <TrainerListItem
          horse={currentHorse}
          onChangeHorse={updateCurrentHorse}
        />
        <Divider light />
        <ListItem className={classes.listItem}>
          <Typography className={classes.listItemLabel}>Owner</Typography>
          <Typography className={classes.listItemValue}>
            {getHorseOwnerOrBreederName(currentHorse, EMPTY_STRING)}
          </Typography>
        </ListItem>
        <Divider light />
        <ListItem className={classes.listItem}>
          <Typography className={classes.listItemLabel}>Barn Number</Typography>
          <div className={classes.listItemContainer}>
            {!isListBarnsLoading && (
              <BarnNumberPicker
                className={classes.pickerItem}
                horse={currentHorse}
                selectedFacilityId={currentHorse.currentFacility?.id}
                startActions={true}
                options={facilityBarns}
                onBarnChange={updateCurrentHorse}
              />
            )}
          </div>
        </ListItem>
        <Divider light />
        <ListItem className={classes.listItem}>
          <Typography className={classes.listItemLabel}>
            Stall Number
          </Typography>
          <div className={classes.listItemContainer}>
            <StallNumberPicker
              className={classes.pickerItem}
              horse={currentHorse}
              selectedFacilityId={currentHorse.currentFacility?.id}
              startActions={true}
              onStallChange={updateCurrentHorse}
            />
          </div>
        </ListItem>
        <Divider light />
        <ListItem className={classes.listItem}>
          <Typography className={classes.listItemLabel}>
            Last Workout
          </Typography>
          <Typography className={classes.listItemValue}>
            {currentHorse.lastWorkoutDate
              ? format(parseISO(currentHorse.lastWorkoutDate), "MMMM dd, yyyy")
              : "None"}
          </Typography>
        </ListItem>
        <Divider light />
        <ListItem className={classes.listItem}>
          <Typography className={classes.listItemLabel}>
            Last Workout Track
          </Typography>
          <Typography className={classes.listItemValue}>
            {currentHorse.lastWorkoutFacility
              ? currentHorse.lastWorkoutFacility.code
              : "None"}
          </Typography>
        </ListItem>
        <Divider light />
        <ListItem className={classes.listItem}>
          <Typography className={classes.listItemLabel}>Last Race</Typography>
          <Typography className={classes.listItemValue}>
            {currentHorse.lastRaceDate
              ? format(parseISO(currentHorse.lastRaceDate), "MMMM dd, yyyy")
              : "None"}
          </Typography>
        </ListItem>
        <Divider light />
        <ListItem className={classes.listItem}>
          <Typography className={classes.listItemLabel}>
            Last Race Track
          </Typography>
          <Typography className={classes.listItemValue}>
            {currentHorse.lastRaceFacility
              ? currentHorse.lastRaceFacility.code
              : "None"}
          </Typography>
        </ListItem>
      </Paper>
    </>
  );
};

export default Information;
